/** Property Data pack draft — legal identity, lease terms, rent by floor plan, pets, parking. */

import {
    PetsPolicyDraft,
    clonePetsPolicy,
    emptyPetsPolicy,
    petsPolicyFromPack,
} from './petPolicy'

export const PROPERTY_DATA_VALUE_KEYS = [
    'legal_property_name',
    'legal_property_address',
    'leasing_manager_email',
    'number_of_installments',
    'lease_start_date',
    'lease_end_date',
    'number_of_keys',
] as const

export type PropertyDataValueKey = typeof PROPERTY_DATA_VALUE_KEYS[number]

export type PropertyDataValues = Record<PropertyDataValueKey, string>

export type FloorPlanRentKind = 'new' | 'renewal'

export type FloorPlanRentEntry = {
    new: string
    renewal: string
}

export type OccupancyPolicyDraft = {
    allow_coed_rooms: boolean
}

export type ParkingPolicyDraft = {
    monthly_fee: string
}

export type PropertyDataDraft = {
    values: PropertyDataValues
    floor_plan_rents: Record<string, FloorPlanRentEntry>
    pets: PetsPolicyDraft
    parking: ParkingPolicyDraft
    occupancy: OccupancyPolicyDraft
}

export function emptyParkingPolicy(): ParkingPolicyDraft {
    return { monthly_fee: '' }
}

export function cloneParkingPolicy(
    parking: ParkingPolicyDraft | null | undefined,
): ParkingPolicyDraft {
    return { monthly_fee: String(parking?.monthly_fee ?? '').trim() }
}

export function parkingPolicyFromPack(raw: unknown): ParkingPolicyDraft {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return emptyParkingPolicy()
    }
    const payload = raw as Record<string, unknown>
    return {
        monthly_fee:
            payload.monthly_fee == null
                ? ''
                : String(payload.monthly_fee).trim(),
    }
}

export function emptyOccupancyPolicy(): OccupancyPolicyDraft {
    return { allow_coed_rooms: false }
}

export function cloneOccupancyPolicy(
    occupancy: OccupancyPolicyDraft,
): OccupancyPolicyDraft {
    return { allow_coed_rooms: Boolean(occupancy?.allow_coed_rooms) }
}

export function occupancyPolicyFromPack(raw: unknown): OccupancyPolicyDraft {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return emptyOccupancyPolicy()
    }
    const payload = raw as Record<string, unknown>
    return {
        allow_coed_rooms: payload.allow_coed_rooms === true,
    }
}

export function emptyFloorPlanRent(): FloorPlanRentEntry {
    return { new: '', renewal: '' }
}

export function cloneFloorPlanRents(
    rents: Record<string, FloorPlanRentEntry>,
): Record<string, FloorPlanRentEntry> {
    const next: Record<string, FloorPlanRentEntry> = {}
    for (const [id, row] of Object.entries(rents)) {
        next[id] = { new: row?.new ?? '', renewal: row?.renewal ?? '' }
    }
    return next
}

export function emptyPropertyDataValues(): PropertyDataValues {
    return {
        legal_property_name: '',
        legal_property_address: '',
        leasing_manager_email: '',
        number_of_installments: '',
        lease_start_date: '',
        lease_end_date: '',
        number_of_keys: '',
    }
}

export function emptyPropertyDataDraft(): PropertyDataDraft {
    return {
        values: emptyPropertyDataValues(),
        floor_plan_rents: {},
        pets: emptyPetsPolicy(),
        parking: emptyParkingPolicy(),
        occupancy: emptyOccupancyPolicy(),
    }
}

export type PrimaryAddressFields =
    | {
          address1?: string | null
          address2?: string | null
          city?: string | null
          state?: string | null
          zip_code?: string | null
      }
    | null
    | undefined

export function formatPrimaryAddress(address: PrimaryAddressFields): string {
    if (!address) return ''
    const street = [address.address1, address.address2]
        .map((part) => String(part ?? '').trim())
        .filter(Boolean)
        .join(', ')
    const city = String(address.city ?? '').trim()
    const region = [address.state, address.zip_code]
        .map((part) => String(part ?? '').trim())
        .filter(Boolean)
        .join(' ')
    const locality = [city, region].filter(Boolean).join(', ')
    return [street, locality].filter(Boolean).join(', ')
}

export function applyPrimaryAddressToDraft(
    draft: PropertyDataDraft,
    address: PrimaryAddressFields,
): PropertyDataDraft {
    return {
        values: {
            ...draft.values,
            legal_property_address: formatPrimaryAddress(address),
        },
        floor_plan_rents: cloneFloorPlanRents(draft.floor_plan_rents),
        pets: clonePetsPolicy(draft.pets),
        parking: cloneParkingPolicy(draft.parking),
        occupancy: cloneOccupancyPolicy(draft.occupancy),
    }
}

function stringifyValue(value: unknown): string {
    if (value == null) return ''
    return String(value).trim()
}

export function parseFloorPlanRentEntry(raw: unknown): FloorPlanRentEntry {
    const entry = emptyFloorPlanRent()
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const obj = raw as Record<string, unknown>
        entry.new = stringifyValue(obj.new ?? obj.rent ?? obj.amount)
        entry.renewal = stringifyValue(obj.renewal)
        return entry
    }
    entry.new = stringifyValue(raw)
    return entry
}

export function propertyDataFromPackDefinition(
    raw: unknown,
): PropertyDataDraft {
    const values = emptyPropertyDataValues()
    const floorPlanRents: Record<string, FloorPlanRentEntry> = {}
    if (!raw || typeof raw !== 'object') {
        return {
            values,
            floor_plan_rents: floorPlanRents,
            pets: emptyPetsPolicy(),
            parking: emptyParkingPolicy(),
            occupancy: emptyOccupancyPolicy(),
        }
    }
    const payload = raw as Record<string, unknown>
    const valuesIn =
        payload.values && typeof payload.values === 'object'
            ? (payload.values as Record<string, unknown>)
            : payload
    for (const key of PROPERTY_DATA_VALUE_KEYS) {
        if (key in valuesIn) {
            values[key] = stringifyValue(valuesIn[key])
        }
    }
    const rentsIn = payload.floor_plan_rents
    if (rentsIn && typeof rentsIn === 'object') {
        for (const [unitId, amount] of Object.entries(
            rentsIn as Record<string, unknown>,
        )) {
            const id = String(unitId).trim()
            if (!/^\d+$/.test(id)) continue
            floorPlanRents[id] = parseFloorPlanRentEntry(amount)
        }
    }
    return {
        values,
        floor_plan_rents: floorPlanRents,
        pets: petsPolicyFromPack(payload.pets),
        parking: parkingPolicyFromPack(payload.parking),
        occupancy: occupancyPolicyFromPack(payload.occupancy),
    }
}

export function propertyDataToPackDefinition(
    draft: PropertyDataDraft,
): Record<string, unknown> {
    return {
        values: { ...draft.values },
        floor_plan_rents: cloneFloorPlanRents(draft.floor_plan_rents),
        pets: clonePetsPolicy(draft.pets),
        parking: cloneParkingPolicy(draft.parking),
        occupancy: cloneOccupancyPolicy(draft.occupancy),
    }
}
